import React from 'react';
import { ChatMarkdown } from '@plerionhq/design-system/unstable';
import { Severity, formatLocale } from '@plerionhq/design-system';
import type { SeverityLevel } from '@plerionhq/design-system';

import { useTranslation } from 'react-i18next';

interface ValueRendererProps {
  value: string | number | boolean | null | undefined;
  label?: string;
  type?: string;
}

// Format date string
const formatDate = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    return date.toLocaleString(formatLocale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return dateStr;
  }
};

// Format number with appropriate precision
const formatNumber = (num: number): string => {
  if (Number.isInteger(num)) {
    return num.toLocaleString();
  }
  return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

// Truncate URL to a reasonable length
const truncateUrl = (url: string, maxLength: number = 70): string => {
  if (url.length <= maxLength) {
    return url;
  }
  const start = url.substring(0, Math.floor(maxLength / 2) - 2);
  const end = url.substring(url.length - Math.floor(maxLength / 2) + 1);
  return `${start}...${end}`;
};

// Check if field should use markdown
const shouldUseMarkdown = (val: unknown, label?: string): boolean => {
  if (!label) {
    return false;
  }
  const markdownFields = ['description', 'message', 'summary'];
  return (
    markdownFields.includes(label.toLowerCase()) && typeof val === 'string'
  );
};

export const ValueRenderer: React.FC<ValueRendererProps> = ({
  value,
  label,
  type,
}) => {
  const { t } = useTranslation('pleri/chat');
  if (value === null) {
    return (
      <span className="inline-flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
        {t('notSet')}
      </span>
    );
  }

  if (value === undefined) {
    return (
      <span className="inline-flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
        {t('notAvailable')}
      </span>
    );
  }

  if (typeof value === 'boolean') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded border px-2 py-1 ${
          value
            ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-700 dark:bg-green-950'
            : 'border-red-200 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950'
        }`}
      >
        {value ? t('true') : t('false')}
      </span>
    );
  }

  if (typeof value === 'number') {
    return (
      <span className="inline-flex items-center gap-1.5 font-medium text-gray-900 dark:text-gray-100">
        {formatNumber(value)}
      </span>
    );
  }

  // Handle string values
  const stringValue = String(value);

  // Check for markdown fields
  if (shouldUseMarkdown(value, label)) {
    return (
      <div className="prose prose-sm max-w-none">
        <ChatMarkdown content={stringValue} />
      </div>
    );
  }

  // Handle severity fields
  if (
    type === 'severity' ||
    (label && label.toLowerCase().includes('severity'))
  ) {
    const severityLevel = stringValue.toLowerCase() as SeverityLevel;
    return <Severity severityLevel={severityLevel} />;
  }

  // Check for date strings
  if (
    type === 'date' ||
    type === 'datetime' ||
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(stringValue)
  ) {
    return (
      <span className="inline-flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
        {formatDate(stringValue)}
      </span>
    );
  }

  // Handle URLs
  if (stringValue.startsWith('http://') || stringValue.startsWith('https://')) {
    return (
      <a
        href={stringValue}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-blue-700 hover:underline"
        title={stringValue}
      >
        {truncateUrl(stringValue)}
      </a>
    );
  }

  // Default string rendering
  return (
    <span className="inline-flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
      {stringValue}
    </span>
  );
};
